import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import {
  CloseOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button, Modal, Pagination, Spin,
} from "antd";
import { toast } from "react-toastify";

import NoResultsGif from "../../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import SwabListExport from "../SwabList/SwabListExport";
import SwabListTable from "../SwabList/SwabListTable";

import { analyticsSampleDataFetch } from "../../../../../actions/envAnalytics";
import { formatResultTestDateRange, normalizeVolumeTrendSampleRows } from "../../utils";

import pumpkinGif from "../../../../../assets/images/environment/pumpkin.gif";
import "../VolumeTrend/SwabListModal.css";
import "./ResultTestSampleModal.css";

const TABLE_HEADERS = ["Sample ID", "Received date", "Sample Type"];
const FINAL_RESULT_COLUMN_LABEL = "Final Result";

const STATUS_FILTERS = {
  all: "all",
  results_available: "results_available",
  in_process: "in_process",
};

// Each status tab maps to a `test_spec` API param; "all" sends none.
const STATUS_FILTER_PARAMS = {
  [STATUS_FILTERS.all]: {},
  [STATUS_FILTERS.results_available]: { test_spec: "result_ready" },
  [STATUS_FILTERS.in_process]: { test_spec: "in_progress" },
};

const formatCount = (value) => String(value ?? 0).padStart(2, "0");

export default function ResultTestSampleModal({
  open,
  onClose,
  dateRange = "",
  fetchParams = null,
  modalTitle = "Sample List",
  summaryCounts = {
    total: 0,
    resultsAvailable: 0,
    inProcess: 0,
  },
}) {
  const [exportOpen, setExportOpen] = useState(false);
  const [swabList, setSwabList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.all);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const abortControllerRef = useRef(null);

  const formattedDateRange = formatResultTestDateRange(dateRange);

  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return swabList.slice(start, start + pageSize);
  }, [swabList, currentPage, pageSize]);

  const exportSwabList = useMemo(
    () => swabList.map((row) => ({
      ...row,
      tests: row.final_result,
    })),
    [swabList],
  );

  const statusTabs = useMemo(() => ([
    { key: STATUS_FILTERS.all, label: "All", count: summaryCounts.total },
    {
      key: STATUS_FILTERS.results_available,
      label: "Results available",
      count: summaryCounts.resultsAvailable,
    },
    {
      key: STATUS_FILTERS.in_process,
      label: "In Process",
      count: summaryCounts.inProcess,
    },
  ]), [summaryCounts]);

  useEffect(() => {
    setStatusFilter(STATUS_FILTERS.all);
  }, [fetchParams]);

  useEffect(() => {
    if (!open || !fetchParams) {
      return undefined;
    }

    const fetchSamples = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsLoading(true);

      try {
        const response = await analyticsSampleDataFetch(
          {
            ...fetchParams,
            ...STATUS_FILTER_PARAMS[statusFilter],
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;

        if (response.success) {
          setSwabList(normalizeVolumeTrendSampleRows(response.result));
        } else if (!response.canceled) {
          setSwabList([]);
          toast.error("Failed to load sample list");
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          toast.error("Error loading sample list");
          setSwabList([]);
        }
      } finally {
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
        }
      }
    };

    fetchSamples();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [open, fetchParams, statusFilter]);

  useEffect(() => {
    if (!open) {
      setSwabList([]);
      setExportOpen(false);
      setIsLoading(false);
      setStatusFilter(STATUS_FILTERS.all);
    }
  }, [open]);

  useEffect(() => {
    setCurrentPage(1);
  }, [swabList]);

  const handleExport = () => {
    setExportOpen(true);
  };

  const handleCloseExport = () => {
    setExportOpen(false);
  };

  return (
    <>
      <Modal
        open={open}
        closable={false}
        centered
        footer={null}
        onCancel={onClose}
        className="SwabListModal SwabListModal--resultTest"
        width="960px"
        destroyOnHidden
      >
        <div className="SwabListModal__Header">
          <span className="SwabListModal__HeaderTitle">{modalTitle}</span>
          <CloseOutlined
            className="SwabListModal__CloseIcon"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
        <div className="SwabListModal__Toolbar SwabListModal__Toolbar--resultTest">
          <span className="SwabListModal__DateLabel">
            Date Range :
            {" "}
            <span className="SwabListModal__DateValue">{formattedDateRange}</span>
          </span>
          <div className="ResultTestSampleModal__StatusTabs">
            <div className="ResultTestSampleModal__StatusTabsInner">
              {statusTabs.map(({ key, label, count }) => (
                <button
                  key={key}
                  type="button"
                  className={`ResultTestSampleModal__StatusTab${
                    statusFilter === key ? " ResultTestSampleModal__StatusTab--active" : ""
                  }`}
                  onClick={() => setStatusFilter(key)}
                >
                  {label}
                  {" "}
                  (
                  {formatCount(count)}
                  )
                </button>
              ))}
            </div>
          </div>
          <Button
            type="default"
            className="SwabListModal__ExportButton"
            icon={<UploadOutlined />}
            onClick={handleExport}
            disabled={isLoading || swabList.length === 0}
          >
            Export
          </Button>
        </div>
        <div className="SwabListModal__Content SwabListModal__Content--resultTest">
          <StyledScrollbar className="SwabListModal__ContentScrollbar">
            {isLoading ? (
              <div className="SwabListModal__Loading">
                <Spin indicator={<LoadingOutlined spin />} />
              </div>
            ) : swabList.length > 0 ? (
              <SwabListTable
                data={paginatedList}
                includeTests={false}
                includeFinalResult
                scrollY={false}
              />
            ) : (
              <NoResultsGif message="No Data" image={pumpkinGif} className="SwabListModal__NoResults" />
            )}
          </StyledScrollbar>
          {!isLoading && swabList.length > 0 && (
            <Pagination
              className="SwabListModal__Pagination"
              current={currentPage}
              pageSize={pageSize}
              total={swabList.length}
              size="small"
              showSizeChanger
              pageSizeOptions={[10]}
              hideOnSinglePage
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
            />
          )}
        </div>
      </Modal>
      <SwabListExport
        open={exportOpen}
        onClose={handleCloseExport}
        swabList={exportSwabList}
        tableHeaders={TABLE_HEADERS}
        testsColumnLabel={FINAL_RESULT_COLUMN_LABEL}
        modalTitle={modalTitle}
        dateRange={formattedDateRange}
        includeReceivedDate={false}
      />
    </>
  );
}
